import { z } from "zod";
import { slideOverlayOverridesSchema } from "./slideshow-overlay";

/** Public MCP contract for the native Slideshow Express product.  This is
 * intentionally one semantic tool: it keeps the lifecycle discoverable without
 * making provider configuration or a second rendering API part of MCP. */
export const MCP_SLIDESHOW_EXPRESS_LIMITS = Object.freeze({
  variantsPerBatch: 5,
  slidesPerVariant: 5,
  aspectRatio: "4:5",
});

export const SLIDESHOW_EXPRESS_CANVAS = Object.freeze({
  width: 1080,
  height: 1350,
  aspectRatio: MCP_SLIDESHOW_EXPRESS_LIMITS.aspectRatio,
});

export function slideshowExpressFixedOutputWarning(): string {
  return `Each creation always starts exactly ${MCP_SLIDESHOW_EXPRESS_LIMITS.variantsPerBatch} variants of exactly ${MCP_SLIDESHOW_EXPRESS_LIMITS.slidesPerVariant} ${MCP_SLIDESHOW_EXPRESS_LIMITS.aspectRatio} slides.`;
}

export const MCP_SLIDESHOW_EXPRESS_CATALOG = [{
  name: "personaos_slideshow_express",
  description: "Use the native Slideshow Express lifecycle for an accessible brand: discover active styles and fixed limits; create exactly five 4:5 five-slide variants; inspect durable runs, reviews, slides and package copy; approve or safely regenerate review stages; retry failed work; update a ready slide overlay without rerendering; inspect delivery/save outcomes; or delete an inactive run. For completed wording or positioning revisions, prefer personaos_slideshow_revise_completed. This never exposes provider workflow URLs, credentials, raw prompts, or Creative Strategist.",
  annotations: { readOnlyHint: false, destructiveHint: true, openWorldHint: false },
}, {
  name: "personaos_slideshow_revise_completed",
  description: "Revise text and/or positioning on an already completed five-slide Slideshow Express run without an image-generation provider. First inspect the run, then provide both current version fences, an explicit all/selected stable-slide target, the complete five-string approved copy when wording changes, optional positioning/style overrides, and an idempotency key. Returns all five promoted image previews.",
  annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: false },
}] as const;

const completedRevisionBaseSchema = z.object({
  brandId: z.string().trim().min(1).max(200),
  runId: z.string().trim().min(1).max(200),
  target: z.enum(["all", "selected"]),
  targetSlideIds: z.array(z.string().trim().regex(/^slide-0[1-5]$/)).min(1).max(5).optional(),
  approvedCopy: z.array(z.string().trim().min(1).max(500)).length(5).optional(),
  overrides: slideOverlayOverridesSchema.nullable().optional(),
  expectedCopyVersion: z.number().int().min(1),
  expectedRevisionVersion: z.number().int().min(0),
  idempotencyKey: z.string().trim().min(1).max(160),
  retry: z.boolean().optional(),
  grantId: z.string().trim().min(1).max(200).optional(),
}).strict();

export const mcpSlideshowCompletedRevisionInputSchema = completedRevisionBaseSchema.superRefine((value, ctx) => {
  if (value.target === "selected" && !value.targetSlideIds?.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "targetSlideIds are required when target is selected", path: ["targetSlideIds"] });
  }
  if (value.target === "all" && value.targetSlideIds?.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "targetSlideIds cannot be used with target all", path: ["targetSlideIds"] });
  }
  if (!value.approvedCopy && value.overrides === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Provide approvedCopy for wording or overrides for presentation", path: ["approvedCopy"] });
  }
});

const action = z.enum([
  "discover", "list_runs", "inspect", "review", "create",
  "approve_concept", "regenerate_concept", "approve_copy", "regenerate_copy",
  "approve_storyboard", "regenerate_storyboard", "retry_storyboard",
  "retry_run", "retry_slide", "update_overlay", "revise_completed", "delivery", "save", "delete",
  // Content-plan lifecycle. These names intentionally sit alongside the
  // established run-level commands: a plan dispatches the same native runs.
  "import", "revise", "add_item", "remove_item", "prepare", "authorize", "execute",
  "pause", "resume", "revoke", "final_selection", "events",
]);

export const mcpSlideshowContentItemSchema = z.object({
  id: z.string().trim().min(1).max(160).optional(),
  wording: z.string().min(1).max(8000),
  scheduledFor: z.string().datetime({ offset: true }).optional().nullable(),
  styleId: z.string().trim().min(1).max(160).optional().nullable(),
  productId: z.string().trim().min(1).max(200).optional().nullable(),
  offerId: z.string().trim().min(1).max(200).optional().nullable(),
  personaId: z.string().trim().min(1).max(200).optional().nullable(),
  hook: z.string().trim().min(1).max(2000).optional().nullable(),
  topic: z.string().trim().min(1).max(2000).optional().nullable(),
  direction: z.string().trim().min(1).max(4000).optional().nullable(),
  ctaText: z.string().trim().min(1).max(300).optional().nullable(),
  narrativeAngleId: z.string().trim().min(1).max(200).optional().nullable(),
  references: z.array(z.string().trim().min(1).max(200)).max(20).optional(),
  facts: z.array(z.string().trim().min(1).max(4000)).max(50).optional(),
  policies: z.array(z.string().trim().min(1).max(4000)).max(50).optional(),
}).strict().superRefine((value, ctx) => {
  if (value.productId && value.offerId) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["offerId"], message: "Choose either productId or offerId, not both" });
});

export const mcpSlideshowExpressInputSchema = z.object({
  brandId: z.string().trim().min(1).max(200),
  action,
  runId: z.string().trim().min(1).max(200).optional(),
  slideId: z.string().trim().min(1).max(200).optional(),
  styleId: z.string().trim().min(1).max(160).optional(),
  name: z.string().trim().max(300).optional(),
  customConcept: z.string().trim().max(4000).nullable().optional(),
  ctaText: z.string().trim().max(300).nullable().optional(),
  personaId: z.string().trim().min(1).max(200).nullable().optional(),
  productId: z.string().trim().min(1).max(200).nullable().optional(),
  offerId: z.string().trim().min(1).max(200).nullable().optional(),
  narrativeAngleId: z.string().trim().min(1).max(200).nullable().optional(),
  concept: z.string().trim().min(1).max(8000).optional(),
  slideTexts: z.array(z.string().trim().min(1).max(500)).min(1).max(20).optional(),
  selectedOptionIndex: z.number().int().min(0).optional(),
  text: z.string().trim().min(1).max(500).optional(),
  overrides: slideOverlayOverridesSchema.nullable().optional(),
  /** Completed-run revision target. `all` is explicit; selected IDs are stable
   * slide IDs (slide-01..slide-05), never database record IDs. */
  target: z.enum(["all", "selected"]).optional(),
  targetSlideIds: z.array(z.string().trim().regex(/^slide-0[1-5]$/)).min(1).max(5).optional(),
  /** A wording revision is always a complete canonical five-string package,
   * even when only one slide changed. Equal strings are intentionally valid. */
  approvedCopy: z.array(z.string().trim().min(1).max(500)).length(5).optional(),
  expectedCopyVersion: z.number().int().min(1).optional(),
  expectedRevisionVersion: z.number().int().min(0).optional(),
  idempotencyKey: z.string().trim().min(1).max(160).optional(),
  planId: z.string().trim().min(1).max(200).optional(),
  grantId: z.string().trim().min(1).max(200).optional(),
  version: z.number().int().min(1).optional(),
  items: z.array(mcpSlideshowContentItemSchema).min(1).max(100).optional(),
  itemIds: z.array(z.string().trim().min(1).max(160)).min(1).max(100).optional(),
  planName: z.string().trim().min(1).max(255).optional(),
  retry: z.boolean().optional(),
  saveToLibrary: z.boolean().optional(),
  stages: z.array(z.enum(["dispatch", "concept", "copy", "storyboard", "render", "save"])).min(1).max(6).optional(),
  expiresAt: z.string().datetime({ offset: true }).optional(),
  maxRetries: z.number().int().min(0).max(10).optional(),
  maxRegenerations: z.number().int().min(0).max(10).optional(),
  maxCredits: z.number().min(0).max(1_000_000).optional(),
  maxSpendUsd: z.number().min(0).max(1_000_000).optional(),
  illustrativeAlternative: z.boolean().optional(),
  selectionCriteria: z.object({
    preferredVariantIndex: z.number().int().min(1).max(5),
  }).strict().optional(),
  contentPolicyScope: z.array(z.enum(["brand-approved-copy", "no-social-publishing"])).length(2).optional(),
  cursor: z.string().trim().min(1).max(200).optional(),
  limit: z.number().int().min(1).max(100).optional(),
}).strict().superRefine((value, ctx) => {
  const planActions = ["import", "inspect", "revise", "add_item", "remove_item", "prepare", "authorize", "execute", "pause", "resume", "revoke", "final_selection"];
  const needsRun = !["discover", "list_runs", "create", "events", ...planActions].includes(value.action);
  if (needsRun && !value.runId) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "runId is required for this action", path: ["runId"] });
  if (planActions.includes(value.action) && value.action !== "import" && value.action !== "inspect" && !value.planId) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "planId is required for this action", path: ["planId"] });
  if (value.action === "inspect" && !value.planId && !value.runId) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "planId or runId is required for inspect", path: ["planId"] });
  if (["revise", "add_item", "remove_item", "prepare", "authorize", "execute", "pause", "resume", "revoke", "final_selection"].includes(value.action) && value.version == null) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "version is required for this action", path: ["version"] });
  if (value.action === "authorize" && (!value.itemIds?.length || !value.stages?.length || !value.expiresAt || value.maxRetries == null || value.maxRegenerations == null || (!value.maxCredits && !value.maxSpendUsd) || !value.contentPolicyScope?.length || !value.selectionCriteria)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "authorize requires itemIds, stages, expiresAt, retry/regeneration caps, a budget, contentPolicyScope, and one preferredVariantIndex", path: ["itemIds"] });
  if (value.action === "authorize" && (
    !value.contentPolicyScope?.includes("brand-approved-copy") ||
    !value.contentPolicyScope?.includes("no-social-publishing") ||
    new Set(value.contentPolicyScope).size !== 2
  )) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "authorize requires exactly brand-approved-copy and no-social-publishing policy scope", path: ["contentPolicyScope"] });
  if (["import", "revise"].includes(value.action) && !value.items?.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "items are required for this action", path: ["items"] });
  if (value.action === "add_item" && !value.items?.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "items are required to add content", path: ["items"] });
  if (["retry_slide", "update_overlay"].includes(value.action) && !value.slideId) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "slideId is required for this action", path: ["slideId"] });
  if (value.action === "revise_completed") {
    if (value.expectedCopyVersion == null) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expectedCopyVersion is required for a completed revision", path: ["expectedCopyVersion"] });
    if (value.expectedRevisionVersion == null) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expectedRevisionVersion is required for a completed revision", path: ["expectedRevisionVersion"] });
    if (!value.target) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "target must be all or selected", path: ["target"] });
    if (value.target === "selected" && !value.targetSlideIds?.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "targetSlideIds are required when target is selected", path: ["targetSlideIds"] });
    if (value.target === "all" && value.targetSlideIds?.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "targetSlideIds cannot be used with target all", path: ["targetSlideIds"] });
    if (!value.approvedCopy && value.overrides === undefined) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Provide approvedCopy for wording or overrides for presentation", path: ["approvedCopy"] });
  }
  if (value.action === "create" && !value.styleId) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "styleId is required to create a batch", path: ["styleId"] });
  if (value.productId && value.offerId) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Choose either productId or offerId, not both", path: ["offerId"] });
  if (!["discover", "list_runs", "inspect", "review", "events"].includes(value.action) && !value.idempotencyKey) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "idempotencyKey is required for mutations", path: ["idempotencyKey"] });
});

export type McpSlideshowExpressInput = z.infer<typeof mcpSlideshowExpressInputSchema>;
